"use client";

import Link from "next/link";
import Image from "next/image";
import { usePathname } from "next/navigation";

import { X, Youtube, Telegram, Button, GoPulseX } from "@/components";
import { MAIN_NAV } from "@/constants/nav";
import { cn } from "@/utils";

const SOCIAL = [
  { icon: X, href: "https://twitter.com/lookintohex", label: "X" },
  { icon: Youtube, href: "https://www.youtube.com/gerardog", label: "YouTube" },
  { icon: Telegram, href: "https://t.me/lookintohex", label: "Telegram" },
];

export function Navbar() {
  const pathname = usePathname();
  if (pathname.startsWith("/auth")) return null;

  return (
    <header className="nav-shell sticky top-0 z-50 -mx-4 mb-0 px-4 sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
      <div className="nav-backdrop" aria-hidden />
      <div className="nav-backdrop-shine" aria-hidden />

      <div className="relative flex h-16 items-center gap-6">
        {/* Logo */}
        <Link href="/" className="group flex shrink-0 items-center gap-3">
          <div className="nav-logo-frame">
            <div className="nav-logo-inner">
              <Image
                src="/brand-logo.png"
                alt="LookIntoHex"
                width={32}
                height={32}
                className="h-7 w-7 transition-transform duration-300 group-hover:scale-105"
              />
            </div>
          </div>
          <span className="hidden font-bold tracking-tight hex-gradient-text sm:block">
            LookIntoHex
          </span>
        </Link>

        {/* Primary nav — single source of truth */}
        <nav
          className="hidden flex-1 items-center justify-center gap-0.5 lg:flex"
          aria-label="Main"
        >
          {MAIN_NAV.map(({ href, label }) => {
            const active =
              href === "/"
                ? pathname === "/"
                : pathname === href || pathname.startsWith(`${href}/`);
            return (
              <Link
                key={href}
                href={href}
                className={cn(
                  "relative rounded-lg px-3.5 py-2 text-sm font-medium transition-colors duration-200",
                  active ? "text-white" : "text-muted hover:text-white",
                )}
              >
                {active && (
                  <span className="absolute inset-x-3 bottom-1.5 h-0.5 hex-gradient rounded-full shadow-[0_0_10px_rgba(255,15,111,0.45)]" />
                )}
                {label}
              </Link>
            );
          })}
        </nav>

        {/* Actions */}
        <div className="ml-auto flex items-center gap-2 sm:gap-3">
          <div className="hidden items-center gap-3 md:flex md:pr-1">
            {SOCIAL.map(({ icon: Icon, href, label }) => (
              <Link
                key={href}
                href={href}
                target="_blank"
                aria-label={label}
                className="text-muted transition-colors hover:text-hex-400"
              >
                <Icon />
              </Link>
            ))}
          </div>
          <GoPulseX />
          <Link href="https://go.hex.com" target="_blank" rel="noopener noreferrer">
            <Button variant="primary" aria-label="Stake Hex">
              Stake
            </Button>
          </Link>
        </div>
      </div>

      {/* Mobile nav scroll */}
      <nav
        className="relative flex gap-1 overflow-x-auto pb-4 pt-0.5 lg:hidden no-scrollbar"
        aria-label="Main mobile"
      >
        {MAIN_NAV.map(({ href, label }) => {
          const active =
            href === "/"
              ? pathname === "/"
              : pathname === href || pathname.startsWith(`${href}/`);
          return (
            <Link
              key={href}
              href={href}
              className={cn(
                "shrink-0 rounded-full px-4 py-1.5 text-sm font-medium transition-colors",
                active
                  ? "bg-white/10 text-white"
                  : "bg-black/20 text-muted hover:text-white",
              )}
            >
              {label}
            </Link>
          );
        })}
      </nav>
    </header>
  );
}

export default Navbar;
